import AppDispatcher from '../dispatcher/AppDispatcher';
import PaymentConstants from '../constants/PaymentConstants';
import ReservationConstants from '../constants/ReservationConstants';
import ReservationStore from '../stores/ReservationStore';
import SearchStore from '../stores/SearchStore';
import ProfileStore from '../stores/ProfileStore';
import ResultsStore from '../stores/ResultsStore';
import PointsStore from '../stores/PointsStore';

import Analytics from 'arbitrip-common/client/analytics';
import { getPointsToggleState } from 'arbitrip-common/client/analytics/services/loyaltyService';
import { getBnplBookingProps } from 'arbitrip-common/client/analytics/services/bnplService';
import { BNPL_DISABLED_REASON, CHECKOUT_FLOW_VARIANT, PLATFORM } from 'arbitrip-common/client/analytics/constants/bnpl';
import { getIsBnpl, computeBnplAmounts } from '@/utils/PayLater';
import HotelStore from '../stores/HotelStore';

// Cheap, action-layer detection of the points-only full-coverage case so a BNPL-eligible booking that
// was auto-switched to credit card because points fully covered the price reports
// fully_covered_by_points on BOOKING_CREATED. Coupon-driven coverage is not detectable here (no coupon
// store at the action layer) and falls through to the deadline_too_close auto-derivation.
function getBnplFullyCoveredByPointsReason(reservation, points) {
	if (!reservation || reservation.bnpl_eligible !== true || reservation.bnpl_ils_amount == null) {
		return null;
	}
	const user_chosen_points = points?.user_chosen_points > 0 ? points.user_chosen_points : 0;
	const points_ex_rate = points?.points_ex_rate > 0 ? points.points_ex_rate : 0;
	if (!(user_chosen_points > 0 && points_ex_rate > 0)) {
		return null;
	}
	const amounts = computeBnplAmounts({
		bnplIlsAmount: reservation.bnpl_ils_amount,
		bnplIlsRate: reservation.bnpl_ils_rate,
		bnplFeePercentage: reservation.bnpl_fee_percentage ?? 2,
		pointsDiscountIls: user_chosen_points * points_ex_rate,
		pointIlsValue: points_ex_rate,
	});
	return amounts && amounts.isFullyCovered ? BNPL_DISABLED_REASON.FULLY_COVERED_BY_POINTS : null;
}

export default {
	clearPaymentData: function () {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.CLEAR_PAYMENT_DATA,
		});
	},

	setPaymentErrorMessage: function (data) {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.PAYMENT_ERROR_MESSAGE,
			data,
		});
	},

	setLoyaltyCardError: function () {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.LOYALTY_CARD_ERROR,
		});
	},

	markPaymentAsCharged: function (coupon_validation) {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.CREDIT_PAYMENT_CHARGED,
		});

		const search_terms = SearchStore.getSearchTerms();
		const reservation = ReservationStore.getReservationData();
		const profile = ProfileStore.getProfile();
		const hotel = HotelStore.getHotelData();
		const hotel_info = ResultsStore.getHotelById(hotel.id);
		const distanceInKm = hotel_info?.distanceInKm ?? 0;
		const points_from_store = PointsStore.getPoints();
		const points = {
			...points_from_store,
			...(!points_from_store?.points_ex_rate &&
				profile?.loyalty?.arbitrip_points_to_currency_exchange_rate && {
					points_ex_rate: profile.loyalty.arbitrip_points_to_currency_exchange_rate,
				}),
		};

		const priceChangedData = reservation.deal?.priceChangedData;
		const triggered_by_rate_check = !!priceChangedData?.confirmedChange;
		const rate_check_change_types = triggered_by_rate_check
			? (priceChangedData.newPrice > priceChangedData.oldPrice ? ['price_increase'] : ['price_decrease'])
			: null;

		const isBnpl = getIsBnpl(reservation);
		const payment_method = isBnpl
			? PaymentConstants.PAYMENT_METHODS_ANALYTICS.BNPL
			: PaymentConstants.PAYMENT_METHODS_ANALYTICS.CREDIT_CARD;

		Analytics.actions.responses.bookingCreated({
			user: profile,
			hotel,
			search: search_terms,
			deal: reservation.deal,
			points,
			payment_method,
			distance: distanceInKm,
			...ResultsStore.getReservationAttemptsObject(hotel.id),
			booking_id: reservation.short_id,
			status: 'success',
			triggered_by_rate_check,
			rate_check_change_types,
			points_toggle_state_at_booking: getPointsToggleState(ResultsStore.getShowPricesInPoints()),
			charge_currency: reservation.deal?.currency,
			coupon: coupon_validation,
			...(reservation.bnpl_eligible
				? {
						bnpl: getBnplBookingProps({
							reservation,
							deal: reservation.deal,
							bnpl_shown: !!profile.credit_card_allowed && reservation.bnpl_eligible === true,
							checkout_flow_variant: CHECKOUT_FLOW_VARIANT.DESKTOP,
							platform: PLATFORM.DESKTOP,
							usedBnpl: isBnpl,
							bnpl_disabled_reason: getBnplFullyCoveredByPointsReason(reservation, points),
						}),
					}
				: {}),
		});
		ResultsStore.cleanReservationAttempts();
	},

	save3DSInformation: function (data) {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.THREEDS_INFORMATION_DATA,
			data,
		});
	},

	markPaymentAsFailed: function (err) {
		AppDispatcher.handleAction({
			actionType: PaymentConstants.CREDIT_PAYMENT_FAILED,
			data: { error_message: err },
		});

		let reservation = ReservationStore.getReservationData();
		let profile = ProfileStore.getProfile();
		ResultsStore.increaseReservationAttempts(reservation.hotel?.id);
		Analytics.actions.responses.reservationFailed({
			user: profile,
			hotel: reservation.hotel,
			deal: reservation.deal,
			payment_method: PaymentConstants.PAYMENT_METHODS_ANALYTICS.CREDIT_CARD,
			error: err,
			status: reservation.status,
			failure_reason: err,
			...ResultsStore.getReservationAttemptsObject(reservation.hotel?.id),
		});
	},

	markPaymentAsCannotBeOpened: function () {
		AppDispatcher.handleAction({
			actionType: ReservationConstants.ADVANCE_RESERVATION_FAILURE,
		});
	},
};
